import { useField } from "formik";
import styles from "./DynamicField.module.css";
import CustomDropdown from "../CustomDropdown/CustomDropdown";
import IconPackSelector from "../IconPackSelector/IconPackSelector";
import IconPickSelector from "../IconPickSelector/IconPickSelector";
import { fieldRenderers } from "./fieldRenderers";
import { Field, SelectField, RangeField } from "../../types/types.form";
import { faFont, faSun, faTextHeight } from "@fortawesome/free-solid-svg-icons";
import { useSound } from "../../context/SoundContext";
import { Box, Slider, Typography } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { useLanguage } from "../../context/LanguageContext";
import {postUser} from "../../helper/http.ts";
import SearchableDropdown from "../SearchableDropdown/SearchableDropdown.tsx";

interface DynamicFieldProps {
  field: Field,
}

export default function DynamicField({ field }: DynamicFieldProps) {
  const [input, meta, helpers] = useField(field.name);
  const { value } = input;
  const { error, touched } = meta;
  const { setValue } = helpers;
  const { playSound, playTickSound } = useSound();
  const { t } = useLanguage(); 
  const [ options, setOptions ] = useState<{label: string, value: string}[]>([]);

    useEffect(() => {
        if (field.type === 'fk') {
            postUser('crud/select', {
                "module_id": +field.relationModel,
            }).then(
                res => {
                    setOptions(Object.keys(res.list).map((key: string) => ({ label: res.list[key], value: key })))
                }
            );

        }

    }, [field.customType, field.name]);

  if (field.name === "soundVolume" && field.type === "range") {
    const [localValue, setLocalValue] = useState(value);
    const lastTickValueRef = useRef(value);
    const tickThrottleRef = useRef<number>(0);

    useEffect(() => { 
      setLocalValue(value); 
    }, [value]);

    return (
      <div className={styles.fieldWrapper}>
        <Box sx={{ width: "100%", px: 1 }}>
          <Typography gutterBottom>
            {t(field.label)}: {localValue}%
          </Typography>
          <Slider
            value={localValue}
            onChange={(_, newValue) => {
              const vol = newValue as number;
              setLocalValue(vol);
              
              const step = (field as RangeField).step || 1;
              const diff = Math.abs(vol - lastTickValueRef.current);
              const now = Date.now();
              const timeSinceLastTick = now - tickThrottleRef.current;
              
              if (diff >= step && timeSinceLastTick >= 40) {
                playTickSound(vol);
                lastTickValueRef.current = vol;
                tickThrottleRef.current = now;
              }
            }}
            onChangeCommitted={(_, newValue) => {
              setValue(newValue);
              
              setTimeout(() => {
                playSound("test", newValue as number);
              }, 50);
            }}
            min={(field as RangeField).min || 0}
            max={(field as RangeField).max || 100}
            step={(field as RangeField).step || 1}
            marks
            valueLabelDisplay="auto"
          />
        </Box>
      </div>
    );
  }

  if (field.customType === "themeSelector")
    return (
      <CustomDropdown
        label={t("applicationTheme")}
        options={(field as SelectField).options.map((opt) => ({
            label: t(opt), 
            value: opt,    
          }))}
        value={value ?? ""}
        onChange={setValue}
        error={error}
        touched={touched}
        icon={faSun}
      />
    );

  if (field.customType === "fontSizeSelector")
    return (
      <CustomDropdown
        label={t("fontSize")}
        options={(field as SelectField).options.map(opt => ({
                label: t(opt),
                value: opt,
              }))}
        value={value ?? ""}
        onChange={setValue}
        error={error}
        touched={touched}
        icon={faTextHeight}
      />
    );

  if (field.customType === "fontFamilySelector")
    return (
      <CustomDropdown
        label={t("fontFamily")}
        options={(field as SelectField).options.map(opt => ({
                label: t(opt),
                value: opt,
              }))}
        value={value ?? ""}
        onChange={setValue}
        error={error}
        touched={touched}
        icon={faFont}
      />
    );

  if (field.name === "language") {
    return (
      <CustomDropdown
        label={t("language")}
        options={[
          { label: "English", value: "en" },
          { label: "Русский", value: "ru" },
          { label: "Українська", value: "uk" },
        ]}
        value={value ?? "en"}
        onChange={setValue}
        error={error}
        touched={touched}
      />
    );
  }
  
  if (field.type === "fk") {
    const handleSearch = async (text: string) => {
      const payload = {
        module_id: +field.relationModel,
        filter: text      
      };

      const res = await postUser("crud/select", payload);

      setOptions(
        Object.keys(res.list).map((key) => ({
          label: res.list[key],
          value: key,
        }))
      );
    };

    return (
      <SearchableDropdown
        label={t(field.label)}
        options={options}
        value={value ?? ""}
        onChange={setValue}
        onSearch={handleSearch}   
      />
    );
  }

  if (field.customType === "iconPackSelector")
    return (
      <IconPackSelector
        value={value}
        onChange={setValue}
        error={error}
        touched={touched}
      />
    );

  if (field.customType === "iconPickSelector")
    return <IconPickSelector value={value} onChange={setValue} />;

  const Renderer = field.type ? fieldRenderers[field.type] : undefined;
  if (!Renderer) return null;

  return (
    <div className={styles.fieldWrapper}>
      {Renderer(
        field,
        value,
        setValue,
        error,
        touched,
        t,
        input.onBlur,
        (field as any)?.disabled ?? false
      )}
    </div>
  );
}